import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { GlobalEntityAutocomplete, EntityOption } from './GlobalEntityAutocomplete';
import { UserRequirement } from '../types';

interface URSAutoCompleteProps {
  projectId: string;
  label: string;
  placeholder?: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  currentEntityId?: string; // ID of the entity being edited (e.g., FS-001)
  currentEntityType?: 'FS' | 'TEST' | 'RISK' | 'SOP'; // Type of entity being edited
}

export const URSAutoComplete: React.FC<URSAutoCompleteProps> = ({
  projectId,
  label,
  placeholder,
  selectedIds,
  onChange,
  currentEntityId,
  currentEntityType
}) => {
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [readOnlyIds, setReadOnlyIds] = useState<string[]>([]);

  useEffect(() => {
    const loadURS = async () => {
      if (!projectId) {
        return;
      }

      try {
        const col = collection(db, 'projects', projectId, 'urs');
        const snapshot = await getDocs(col);
        const loaded: EntityOption[] = snapshot.docs.map(doc => {
          const data = doc.data() as UserRequirement;
          return {
            id: data.id || `URS-${doc.id.substring(0, 8)}`,
            label: data.title || data.id || `URS-${doc.id.substring(0, 8)}`,
            title: data.title
          };
        });
        setOptions(loaded);
      } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error loading URS for autocomplete:', err);
      }
        setOptions([]);
      }
    };

    loadURS();
  }, [projectId]);

  // Query for reverse relationships
  // When editing an FS/TEST/RISK/SOP, find URS that have this entity in their links
  useEffect(() => {
    const queryReverseLinks = async () => {
      if (!projectId || !currentEntityId || !currentEntityType) {
        setReadOnlyIds([]);
        return;
      }

      try {
        const reverseIds: string[] = [];
        
        // Query all URS documents to find which ones reference the current entity
        const ursCol = collection(db, 'projects', projectId, 'urs');
        const ursSnapshot = await getDocs(ursCol);
        ursSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const links = data.links || {};
          
          // Check if this URS references the current entity
          if (currentEntityType === 'FS' && links.fs_ids?.includes(currentEntityId)) {
            const id = data.id || `URS-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'TEST' && links.test_ids?.includes(currentEntityId)) {
            const id = data.id || `URS-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'RISK' && links.risk_ids?.includes(currentEntityId)) {
            const id = data.id || `URS-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'SOP' && links.sop_ids?.includes(currentEntityId)) {
            const id = data.id || `URS-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          }
        });

        setReadOnlyIds(reverseIds);
      } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error querying reverse links for URS:', err);
      }
        setReadOnlyIds([]);
      }
    };

    queryReverseLinks();
  }, [projectId, currentEntityId, currentEntityType]);

  return (
    <GlobalEntityAutocomplete
      label={label}
      placeholder={placeholder}
      selectedIds={selectedIds}
      options={options}
      onChange={onChange}
      readOnlyIds={readOnlyIds}
    />
  );
};

